import { css, cx, keyframes } from '@emotion/css';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import * as React from 'react';
import { useLocalStorage } from 'react-use';

import { type GrafanaTheme2 } from '@grafana/data';
import { selectors } from '@grafana/e2e-selectors';
import { t } from '@grafana/i18n';
import { Button, Counter, Icon, Tooltip, useStyles2 } from '@grafana/ui';
import { useQueryParams } from 'app/core/hooks/useQueryParams';

import { PANEL_EDITOR_UI_STATE_STORAGE_KEY } from './state/reducers';

export interface OptionsPaneCategoryProps {
  id: string;
  title?: string;
  renderTitle?: (isExpanded: boolean) => React.ReactNode;
  isOpenDefault?: boolean;
  itemsCount?: number;
  forceOpen?: boolean;
  className?: string;
  isNested?: boolean;
  isDashboardSidebar?: boolean;
  children: ReactNode;
  sandboxId?: string;
  /**
   * Actions rendered in the header next to the collapse toggle (e.g. an "add" button).
   */
  headerActions?: React.ReactNode;
  headerActionPlacement?: 'left' | 'right';
  compactIcons?: boolean;
  /**
   * When set will disable category and show tooltip with disabledText on hover
   */
  disabledText?: string | React.ReactElement;
}

export const CATEGORY_PARAM_NAME = 'showCategory' as const;
export const HIGHLIGHT_CATEGORY_PARAM_NAME = 'highlightCategory' as const;

export const OptionsPaneCategory = React.memo(
  ({
    id,
    title,
    children,
    forceOpen,
    isOpenDefault = true,
    renderTitle,
    className,
    itemsCount,
    isNested = false,
    isDashboardSidebar = false,
    sandboxId,
    headerActions,
    headerActionPlacement = 'right',
    compactIcons = false,
    disabledText,
  }: OptionsPaneCategoryProps) => {
    const generatedId = useId();
    const headingId = `${generatedId}-heading`;
    const contentId = `${generatedId}-content`;
    const [savedState, setSavedState] = useLocalStorage(getOptionGroupStorageKey(id), {
      isExpanded: isOpenDefault,
    });

    const isSavedAsExpanded = savedState?.isExpanded ?? isOpenDefault;
    // collapse if there are explicitly no items
    const isExpandedInitialValue = forceOpen || (isSavedAsExpanded && (itemsCount === undefined || itemsCount > 0));

    const [isExpanded, setIsExpanded] = useState(isExpandedInitialValue);
    const [isHighlighted, setIsHighlighted] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const [queryParams, updateQueryParams] = useQueryParams();
    const isOpenFromUrl = queryParams[CATEGORY_PARAM_NAME] === id;
    const isHighlightedFromUrl = queryParams[HIGHLIGHT_CATEGORY_PARAM_NAME] === id;

    // Handle opening by forceOpen param or from URL
    useEffect(() => {
      if ((forceOpen || isOpenFromUrl) && !isExpanded && !disabledText) {
        setIsExpanded(true);
        setTimeout(() => {
          ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    }, [isExpanded, isOpenFromUrl, forceOpen, disabledText]);

    // Close the category when it becomes disabled
    useEffect(() => {
      if (disabledText && isExpanded) {
        setIsExpanded(false);
      }
    }, [disabledText, isExpanded]);

    // remove effect when feature flag grafana.dashboardSettingsRedesign is removed
    useEffect(() => {
      if (!isHighlightedFromUrl) {
        return;
      }

      setIsHighlighted(true);
      setIsExpanded(true);
      updateQueryParams({ [HIGHLIGHT_CATEGORY_PARAM_NAME]: undefined }, true);
    }, [isHighlightedFromUrl, updateQueryParams]);

    // remove effect when feature flag grafana.dashboardSettingsRedesign is removed
    useEffect(() => {
      if (!isHighlighted) {
        return;
      }

      const scrollTimeout = window.setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);

      const highlightTimeout = window.setTimeout(() => {
        setIsHighlighted(false);
      }, 5000);

      return () => {
        window.clearTimeout(scrollTimeout);
        window.clearTimeout(highlightTimeout);
      };
    }, [isHighlighted]);

    const onToggle = useCallback(() => {
      updateQueryParams({ [CATEGORY_PARAM_NAME]: isExpanded ? undefined : id }, true);
      setSavedState({ isExpanded: !isExpanded });
      setIsExpanded(!isExpanded);
    }, [updateQueryParams, isExpanded, id, setSavedState]);

    if (!renderTitle) {
      renderTitle = function defaultTitle(isExpanded: boolean) {
        if (isExpanded || itemsCount === undefined || itemsCount === 0) {
          return title;
        }

        return (
          <span>
            {title} <Counter value={itemsCount} />
          </span>
        );
      };
    }

    const styles = useStyles2(getStyles);
    const collapseIcon = isExpanded ? 'angle-up' : 'angle-down';
    const toggleButton = (
      <Button
        aria-label={
          isExpanded
            ? t('dashboard.options-pane-category.aria-label-collapse', 'Collapse {{title}} category', { title })
            : t('dashboard.options-pane-category.aria-label-expand', 'Expand {{title}} category', { title })
        }
        data-testid={selectors.components.OptionsGroup.toggle(id)}
        type="button"
        fill="text"
        size={compactIcons ? 'sm' : 'md'}
        variant="secondary"
        aria-expanded={isExpanded}
        className={cx(styles.toggleButton, compactIcons && styles.toggleButtonCompact)}
        icon={headerActionPlacement === 'left' ? (isExpanded ? 'angle-down' : 'angle-right') : collapseIcon}
        onClick={onToggle}
      />
    );

    const boxStyles = cx(
      {
        [styles.box]: true,
        [styles.boxNestedSidebar]: isNested && isDashboardSidebar,
        [styles.boxNestedExpanded]: isNested && isExpanded,
      },
      className
    );

    const headerStyles = cx(styles.header, {
      [styles.headerExpanded]: isExpanded,
      [styles.headerNested]: isNested,
      [styles.boxHighlighted]: isHighlighted,
    });

    const bodyStyles = cx(styles.body, {
      [styles.bodyNested]: isNested,
      [styles.bodySidebar]: isDashboardSidebar,
      [styles.bodyNestedSidebar]: isNested && isDashboardSidebar,
    });

    /**
     * Disabled categories just show the disabled header and icon
     */
    if (disabledText) {
      return (
        <div
          className={boxStyles}
          data-plugin-sandbox={sandboxId}
          data-testid={selectors.components.OptionsGroup.group(id)}
          ref={ref}
        >
          <div className={headerStyles}>
            <h3 id={headingId} className={cx(styles.title, styles.titleDisabled)}>
              {renderTitle(isExpanded)}
            </h3>
            <Tooltip interactive={!(typeof disabledText === 'string')} content={disabledText}>
              <Icon size="sm" name="ban" className={styles.disabledIcon} />
            </Tooltip>
          </div>
        </div>
      );
    }

    return (
      <div
        className={boxStyles}
        data-plugin-sandbox={sandboxId}
        data-testid={selectors.components.OptionsGroup.group(id)}
        ref={ref}
      >
        {/* disabling a11y rules here because there's a Button that handles keyboard interaction */}
        {/* this just provides a better experience for mouse users */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className={headerStyles} onClick={onToggle}>
          {headerActionPlacement === 'left' && toggleButton}
          <h3 id={headingId} className={cx(styles.title, isExpanded && styles.titleExpanded)}>
            {renderTitle(isExpanded)}
          </h3>
          {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
          {headerActionPlacement === 'right' && toggleButton}
        </div>
        {isExpanded && (
          <div className={bodyStyles} id={contentId} role="group" aria-labelledby={headingId}>
            {children}
          </div>
        )}
      </div>
    );
  }
);
OptionsPaneCategory.displayName = 'OptionsPaneCategory';

const getStyles = (theme: GrafanaTheme2) => ({
  box: css({
    borderTop: `1px solid ${theme.colors.border.weak}`,
  }),
  boxNestedExpanded: css({
    marginBottom: theme.spacing(2),
  }),
  boxNestedSidebar: css({
    borderTop: 'none',
    padding: theme.spacing(0),
  }),
  title: css({
    flexGrow: 1,
    overflow: 'hidden',
    lineHeight: 1.5,
    fontSize: theme.typography.body.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    margin: 0,
    color: theme.colors.text.secondary,
  }),
  titleExpanded: css({
    color: theme.colors.text.primary,
  }),
  header: css({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    color: theme.colors.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    cursor: 'pointer',
    '&:hover': {
      background: theme.colors.emphasize(theme.colors.background.primary, 0.03),
    },
  }),
  headerExpanded: css({
    color: theme.colors.text.primary,
  }),
  headerNested: css({
    padding: theme.spacing(0.5, 0),
  }),
  toggleButton: css({
    alignSelf: 'baseline',
    justifyContent: 'center',
    '&:focus, &:focus-visible': {
      background: 'none',
    },
  }),
  toggleButtonCompact: css({
    width: theme.spacing(3),
    height: theme.spacing(3),
    padding: 0,
  }),
  headerActions: css({
    display: 'flex',
    alignItems: 'center',
  }),
  body: css({
    padding: theme.spacing(1, 2),
  }),
  bodySidebar: css({
    padding: theme.spacing(0, 1),
  }),
  titleDisabled: css({
    color: theme.colors.text.disabled,
    cursor: 'not-allowed',
  }),
  disabledIcon: css({
    color: theme.colors.text.disabled,
    margin: theme.spacing(1, 1, 1, 0),
  }),
  bodyNested: css({
    position: 'relative',
    paddingRight: 0,
  }),
  bodyNestedSidebar: css({
    '&:before': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: theme.spacing(2),
      width: 1,
      height: '100%',
      background: theme.colors.border.weak,
    },
  }),

  boxHighlighted: css({
    [theme.transitions.handleMotion('no-preference')]: {
      animation: `${categoryHighlight(theme)} 5s ease-out forwards`,
    },
    [theme.transitions.handleMotion('reduce')]: {
      backgroundColor: theme.colors.primary.transparent,
      boxShadow: `inset 0 0 0 1px ${theme.colors.primary.border}`,
    },
  }),
});

const categoryHighlight = (theme: GrafanaTheme2) =>
  keyframes({
    '0%': {
      backgroundColor: theme.colors.primary.transparent,
      boxShadow: `inset 0 0 0 1px ${theme.colors.primary.border}`,
    },
    '100%': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
  });

const getOptionGroupStorageKey = (id: string) => `${PANEL_EDITOR_UI_STATE_STORAGE_KEY}.optionGroup[${id}]`;
